import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Upload, FileSpreadsheet, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function OTUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/ots/template", { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_OT.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch (e) {
      toast.error("Error al descargar plantilla");
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setStats(null);
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const res = await api.post("/ots/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data.preview);
      setStats(res.data.stats);
      toast.success("Archivo procesado. Revisa el preview antes de guardar.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar archivo");
    } finally {
      setUploading(false);
    }
  };

  const confirmCreate = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = { ...preview, estatus: "Pendiente" };
      const res = await api.post("/ots", payload);
      toast.success(`OT ${res.data.folio} creada`);
      navigate(`/ots/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear OT");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setStats(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Cargar OT desde Excel</h1>
        <p className="text-sm text-muted-foreground">Descarga la plantilla, llénala y cárgala para crear una OT.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Download className="h-4 w-4 text-primary" /> 1. Descarga la plantilla
            </CardTitle>
            <CardDescription className="text-xs">Plantilla Excel con los catálogos actuales como listas desplegables.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={downloadTemplate} data-testid="template-download-button" className="w-full">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Descargar plantilla_OT.xlsx
            </Button>
            <ul className="text-xs text-muted-foreground mt-3 space-y-1 list-disc list-inside">
              <li>Hoja "OT_Header": datos generales</li>
              <li>Hoja "OT_Items": partidas (PO, SKU, VPN, UPC, PZ...)</li>
              <li>Hoja "Catalogos": referencia</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Upload className="h-4 w-4 text-primary" /> 2. Carga el archivo lleno
            </CardTitle>
            <CardDescription className="text-xs">Te mostraremos un preview antes de crear la OT.</CardDescription>
          </CardHeader>
          <CardContent>
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md py-8 hover:border-primary/60 transition-colors cursor-pointer bg-muted/20"
              data-testid="file-upload-dropzone"
            >
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm">{file ? file.name : "Haz clic o arrastra tu archivo .xlsx"}</span>
              <span className="text-xs text-muted-foreground mt-1">Solo archivos Excel (.xlsx)</span>
              <input
                id="file-upload"
                ref={inputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={onFileChange}
                className="hidden"
                data-testid="file-upload-input"
              />
            </label>
            {uploading && <p className="text-xs text-muted-foreground mt-2 text-center">Procesando archivo...</p>}
          </CardContent>
        </Card>
      </div>

      {preview && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold">3. Preview - Confirma antes de crear</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={reset}><X className="h-4 w-4 mr-1" /> Cancelar</Button>
                <Button size="sm" onClick={confirmCreate} disabled={saving} data-testid="upload-confirm-button">
                  <Check className="h-4 w-4 mr-1" /> {saving ? "Creando..." : "Crear OT"}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Alert>
              <AlertDescription className="text-xs">
                Se creará una OT con folio autogenerado. Estos son los datos detectados:
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Cliente" value={preview.cliente} />
              <Stat label="Solicitante" value={preview.solicitante} />
              <Stat label="Puerto" value={preview.puerto_entrada} />
              <Stat label="Tipos" value={(preview.tipos_trabajo || []).join(", ")} />
              <Stat label="Fecha Arribo" value={(preview.fecha_arribo || "").slice(0, 10)} mono />
              <Stat label="Centro" value={preview.centro || "-"} />
              <Stat label="Partidas" value={stats?.item_count} mono />
              <Stat label="Total PZ" value={stats?.total_pz} mono />
              {(stats?.total_faltante || 0) > 0 && (
                <div className="p-2 rounded-md bg-destructive/15 border border-destructive/50" data-testid="upload-stat-faltante">
                  <div className="text-[10px] uppercase tracking-wider text-destructive">⚠ Total Faltante</div>
                  <div className="text-sm font-mono font-bold text-destructive">{stats.total_faltante}</div>
                </div>
              )}
            </div>
            <div className="rounded-md border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="text-left uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 px-2">PO</th>
                    <th className="py-2 px-2">SKU</th>
                    <th className="py-2 px-2">VPN</th>
                    <th className="py-2 px-2">UPC</th>
                    <th className="py-2 px-2">Pedim.</th>
                    <th className="py-2 px-2 text-right">PZ</th>
                    <th className="py-2 px-2 text-right">Faltante</th>
                    <th className="py-2 px-2">Comentarios</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.items || []).map((it, i) => {
                    const falt = parseInt(it.faltante) || 0;
                    const isShort = falt > 0;
                    return (
                      <tr key={i} className={`border-t border-border/60 ${isShort ? "bg-destructive/5" : ""}`} data-testid={`upload-preview-row-${i}`}>
                        <td className="py-1.5 px-2 font-mono">{it.po || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.sku || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.vpn || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.upc || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.pedimento || "-"}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{it.pz || 0}</td>
                        <td className={`py-1.5 px-2 text-right font-mono ${isShort ? "text-destructive font-bold" : "text-muted-foreground"}`}>
                          {isShort ? `⚠ ${falt}` : "0"}
                        </td>
                        <td className={`py-1.5 px-2 ${isShort ? "text-destructive" : "text-muted-foreground"}`}>{it.comentarios || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="p-2 rounded-md bg-muted/30 border border-border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "-"}</div>
    </div>
  );
}
